import { Link, useParams } from 'react-router-dom';
import { counterparties } from '../lib/api';
import { useResource } from '../hooks/useResource';
import { formatDateTime } from '../lib/format';
import { Alert, Badge, Card, Copyable, Empty, Spinner, Stat } from '../components/ui';

/**
 * A single counterparty: address header, summary stats, then every interaction
 * with it (onramp deliveries), most recent first.
 */

const BASESCAN_TX = 'https://basescan.org/tx/';
const BASESCAN_ADDR = 'https://basescan.org/address/';

function txDisplay(hash: string): string {
  return `${hash.slice(0, 8)}…${hash.slice(-6)}`;
}

function orderTone(status: string): 'success' | 'warning' | 'danger' | 'neutral' {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'expired') return 'neutral';
  return 'warning';
}

export function CounterpartyDetail() {
  const { address = '' } = useParams();
  const res = useResource(() => counterparties.get(address), [address], { refreshMs: 30000 });
  const cp = res.data;

  return (
    <>
      <div className="page-header">
        <h1>Counterparty</h1>
        <p>
          <Link to="/counterparties">← Counterparties</Link>
        </p>
      </div>

      {res.error && <Alert>{res.error}</Alert>}

      {res.loading && !cp ? (
        <Spinner />
      ) : !cp ? (
        <Empty>Address not found.</Empty>
      ) : (
        <>
          <Card title="Address">
            <div style={{ marginBottom: 12 }}>
              <Copyable value={cp.address} display={cp.address} href={BASESCAN_ADDR + cp.address} />
            </div>
            <div className="stat-grid">
              <Stat
                label="Direction"
                value={<Badge tone="neutral">{cp.direction}</Badge>}
              />
              <Stat label="Interactions" value={cp.interaction_count.toLocaleString()} />
              <Stat label="USDC sent" value={cp.total_usdc_sent} />
              <Stat label="USDC received" value={cp.total_usdc_received} />
              <Stat label="First seen" value={formatDateTime(cp.first_seen)} />
              <Stat label="Last seen" value={formatDateTime(cp.last_seen)} />
            </div>
          </Card>

          <Card title={`${cp.interactions.length} interactions`}>
            {!cp.interactions.length ? (
              <Empty>No interactions with this address.</Empty>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Created</th>
                      <th>Order</th>
                      <th>Type</th>
                      <th>Rate</th>
                      <th>NGN in</th>
                      <th>USDC out</th>
                      <th>Delivery tx</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cp.interactions.map((i) => (
                      <tr key={i.order_id}>
                        <td className="muted">{formatDateTime(i.created_at)}</td>
                        <td>
                          <Copyable value={i.order_id} display={i.order_id.slice(0, 10)} />
                        </td>
                        <td>{i.type}</td>
                        <td className="mono">₦{i.rate}</td>
                        <td className="mono">{i.amount_ngn ? `₦${i.amount_ngn}` : '—'}</td>
                        <td className="mono">{i.amount_usdc ?? '—'}</td>
                        <td>
                          <Copyable
                            value={i.tx_hash}
                            display={i.tx_hash ? txDisplay(i.tx_hash) : undefined}
                            href={i.tx_hash ? BASESCAN_TX + i.tx_hash : undefined}
                          />
                        </td>
                        <td>
                          <Badge tone={orderTone(i.status)}>{i.status}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </>
  );
}
